import { Fragment, useEffect, useState } from 'react';
import { supabase } from '../supabase.js';
import { count, countValue, listCouncils, orDash, webLink } from './adminData.js';
import CampaignEmails from './CampaignEmails.jsx';
import ProviderProspects, { EmailLink, PhoneLink } from './ProviderProspects.jsx';
import { useSort } from './SortableTable.jsx';

/**
 * Our emails to councils and their replies, then the councils with housing providers or a housing team we know of; one council's
 * record when `councilCode` is set.
 */
export default function CouncilsTab({ councilCode = '', onOpen }) {
  if (councilCode) return <CouncilRecord key={councilCode} code={councilCode} onOpen={onOpen}/>;
  return (
    <>
      <CampaignEmails campaign="councils" name="Council"/>
      <h2 className="admin-section">Councils</h2>
      <CouncilList onOpen={onOpen}/>
    </>
  );
}

function HousingTeam({ contact }) {
  if (!contact) return '–';
  const phone = String(contact.phone ?? '').trim();
  const page = webLink(contact.housing_url);
  const ways = [];
  if (phone) ways.push(<PhoneLink key="phone" value={phone}/>);
  if (page) ways.push(<a key="page" className="admin-ext" href={page.href} target="_blank" rel="noreferrer">Housing page</a>);
  return (
    <>
      <div>{orDash(contact.organisation)}</div>
      {ways.length > 0 && <div className="admin-sub">{ways.map((way, index) => <Fragment key={way.key}>{index > 0 && ' · '}{way}</Fragment>)}</div>}
    </>
  );
}

const noRows = [];
const councilSort = {
  council: { value: (row) => row.name || row.code },
  nation: { value: (row) => row.nation },
  providers: { value: (row) => countValue(row.providers), first: 'desc' },
  selfReferral: { value: (row) => countValue(row.self_referral), first: 'desc' },
  contacted: { value: (row) => countValue(row.contacted), first: 'desc' },
  team: { value: (row) => row.contact?.organisation },
  teamEmail: { value: (row) => row.contact?.email },
  // Nothing about tenders is tracked yet; the heading still sorts like every other, ready for when it is.
  tenders: { value: () => null },
};
const regionSort = {
  area: { value: (region) => region.area },
  providers: { value: (region) => countValue(region.providers), first: 'desc' },
  selfReferral: { value: (region) => countValue(region.self_referral), first: 'desc' },
};

function CouncilList({ onOpen }) {
  const [state, setState] = useState({ data: null, error: '' });

  useEffect(() => {
    let live = true;
    listCouncils(supabase).then(
      (data) => { if (live) setState({ data: data || {}, error: '' }); },
      (cause) => { if (live) setState((current) => ({ data: current.data || {}, error: cause.message })); },
    );
    return () => { live = false; };
  }, []);

  const { data, error } = state;
  const { rows, header } = useSort(Array.isArray(data?.rows) ? data.rows : noRows, councilSort);
  const { rows: regions, header: regionHeader } = useSort(Array.isArray(data?.regions) ? data.regions : noRows, regionSort);

  return (
    <>
      {error && <p className="mod-error" role="alert">{error}</p>}
      {data === null
        ? <p>Loading councils…</p>
        : (
          <>
            {rows.length === 0
              ? (!error && <p className="admin-empty">No councils yet.</p>)
              : (
                <div className="mod-table-wrap">
                  <table className="mod-audit admin-councils">
                    <thead>
                      <tr>
                        {header('council', 'Council')}{header('nation', 'Nation')}{header('providers', 'Housing providers')}{header('selfReferral', 'Accept self-referrals')}
                        {header('contacted', 'Contacted before')}{header('team', 'Housing team')}{header('teamEmail', 'Housing team email')}{header('tenders', 'Tenders')}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <tr key={row.code}>
                          <td><button type="button" className="admin-link" onClick={() => onOpen({ tab: 'councils', council: row.code })}>{row.name || row.code}</button></td>
                          <td>{orDash(row.nation)}</td>
                          <td>{count(row.providers)}</td>
                          <td>{count(row.self_referral)}</td>
                          <td>{count(row.contacted)}</td>
                          <td><HousingTeam contact={row.contact}/></td>
                          <td><EmailLink value={row.contact?.email}/></td>
                          {/* Tenders: nothing is tracked yet, so the column is left empty. */}
                          <td/>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            {regions.length > 0 && (
              <>
                <h2 className="admin-section">Areas with no single council</h2>
                <div className="mod-table-wrap">
                  <table className="mod-audit">
                    <thead><tr>{regionHeader('area', 'Area')}{regionHeader('providers', 'Housing providers')}{regionHeader('selfReferral', 'Accept self-referrals')}</tr></thead>
                    <tbody>
                      {regions.map((region) => (
                        <tr key={region.area}>
                          <td>{orDash(region.area)}</td>
                          <td>{count(region.providers)}</td>
                          <td>{count(region.self_referral)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
    </>
  );
}

function CouncilRecord({ code, onOpen }) {
  const [state, setState] = useState({ loaded: false, council: null, error: '' });

  // The list is the only place that knows a council's name and housing team, so the whole list is read and this council picked out.
  useEffect(() => {
    let live = true;
    setState({ loaded: false, council: null, error: '' });
    listCouncils(supabase, { query: '' }).then(
      (data) => { if (live) setState({ loaded: true, council: (Array.isArray(data?.rows) ? data.rows : []).find((row) => row.code === code) || null, error: '' }); },
      (cause) => { if (live) setState({ loaded: true, council: null, error: cause.message }); },
    );
    return () => { live = false; };
  }, [code]);

  const { loaded, council, error } = state;
  const contact = council?.contact || null;
  const page = webLink(contact?.housing_url);
  const site = webLink(contact?.website);
  const fields = contact ? [
    ['Organisation', contact.organisation],
    ['Email', contact.email && <EmailLink value={contact.email}/>],
    ['Phone', contact.phone && <PhoneLink value={contact.phone}/>],
    ['Housing page', page && <a className="admin-ext" href={page.href} target="_blank" rel="noreferrer">{page.domain}</a>],
    ['Website', site && <a className="admin-ext" href={site.href} target="_blank" rel="noreferrer">{site.domain}</a>],
    ['Office', contact.office_address],
    ['Note', contact.note],
  ].filter(([, value]) => value) : [];

  return (
    <>
      {!loaded
        ? <p>Loading this council…</p>
        : (
          <>
            <div className="admin-record-head">
              <h2>{council?.name || code}</h2>
              <div className="admin-head-actions">
                <button type="button" className="button button-secondary" onClick={() => onOpen({ tab: 'councils' })}>← Back to councils</button>
              </div>
            </div>
            {error
              ? <p className="mod-error" role="alert">{error}</p>
              : (
                <>
                  <h2 className="admin-section">Housing team</h2>
                  {fields.length > 0
                    ? <dl className="mod-fields">{fields.map(([label, value]) => <Fragment key={label}><dt>{label}</dt><dd>{value}</dd></Fragment>)}</dl>
                    : <p className="mod-note">No housing team contact on file.</p>}
                </>
              )}
          </>
        )}
      <ProviderProspects council={code} onOpen={onOpen}/>
    </>
  );
}
